import { useEffect, useState } from "react";
import { ref, onValue } from "firebase/database";
import { rtdb } from "@/lib/firebase";
import type { ParsedQr } from "@/lib/qrParse";

export interface Participant {
  id: string;
  lastSeenAt: number;
  source?: "phone" | "tablet";
  nickname?: string;
}

const STALE_THRESHOLD_MS = 90_000; // ~3 missed heartbeats

/** Live list of participants seen in the last ~90s. */
export const useParticipants = (parsed: ParsedQr | null): Participant[] => {
  const [participants, setParticipants] = useState<Participant[]>([]);

  useEffect(() => {
    if (!parsed) return;
    const r = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/participants`);
    const unsub = onValue(r, (snap) => {
      const val = (snap.val() as Record<string, { lastSeenAt: number; source?: "phone" | "tablet"; nickname?: string }>) || {};
      const now = Date.now();
      const list: Participant[] = Object.entries(val)
        .map(([id, p]) => ({
          id,
          lastSeenAt: p.lastSeenAt || 0,
          source: p.source,
          nickname: p.nickname,
        }))
        .filter((p) => now - (p.lastSeenAt || 0) < STALE_THRESHOLD_MS)
        .sort((a, b) => a.lastSeenAt - b.lastSeenAt); // oldest first → stable order
      setParticipants(list);
    });
    return () => unsub();
  }, [parsed?.restoId, parsed?.qrId]);

  return participants;
};
